<template>
    <div>
        <div v-if="paymentPromptText != null && paymentPromptText.length > 0" class="payment-prompt">
            <span>{{ paymentPromptText }}</span>
        </div>
        <div
            class="kiosk-payment-method-selector"
            :class="{ disabled: isDisabled, single: methods.length === 1 }"
            v-if="methods.length > 0"
        >
            <button
                v-for="method in methods"
                :key="method.id"
                type="button"
                class="method"
                :class="{ selected: method.id === selectedMethodId }"
                :data-method-type="method.type"
                :disabled="isDisabled"
                @click="onSelectMethod(method)"
            >
                <div
                    v-if="method.imagePath"
                    class="icon"
                    :style="{ backgroundImage: `url('${method.imagePath}')` }"
                ></div>
                <div v-else-if="hasCardIcon(method)" class="icon">
                    <i class="fal fa-credit-card"></i>
                </div>

                <span class="name">{{ methodName(method) }}</span>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { useLanguageStore } from "@/app/stores/language";
    import type { IPaymentMethod } from "@/data/model/Customer";
    import { PaymentMethodMode, PaymentProvider } from "@/data/model/Enums";
    import type { IMultiLanguageText } from "@/data/model/Shared";
    import { Format } from "@/data/util/Format";
    import { clientLanguage } from "@/shared/localization/language";
    import { computed, ref } from "vue";

    // 2x2 layout
    const MAX_METHODS = 4;

    const props = defineProps<{
        onSelect: (method: IPaymentMethod) => void;
    }>();

    const language = useLanguageStore();

    const paymentPromptText = computed(() => Format.translate(paymentPrompt.value, language.current) ?? "");
    const paymentPrompt = ref<IMultiLanguageText | undefined>(undefined);
    const methods = ref<IPaymentMethod[]>([]);
    const isDisabled = ref(false);
    const selectedMethodId = ref<string | undefined>(undefined);

    function methodName(method: IPaymentMethod) {
        return Format.translate(method.localizedName, clientLanguage()) ?? "";
    }

    function hasCardIcon(method: IPaymentMethod) {
        return (
            method.useCollectiveCardIcon ||
            method.provider === PaymentProvider.Stripe ||
            method.provider === PaymentProvider.Wallee ||
            method.provider === PaymentProvider.PostFinance
        );
    }

    function setPaymentMethods(availableMethods: Array<IPaymentMethod>) {
        // same order as the payment method selector popover: online first, offline last,
        // keeping the configured order within each group. the grid flows row by row (Z-like).
        const online = availableMethods.filter((m) => m.mode === PaymentMethodMode.Online);
        const offline = availableMethods.filter((m) => m.mode === PaymentMethodMode.Offline);

        methods.value = [...online, ...offline].slice(0, MAX_METHODS);
    }

    function setSelectedMethod(methodId?: string) {
        selectedMethodId.value = methodId;
    }

    function setDisabled() {
        isDisabled.value = true;
    }

    function setEnabled() {
        isDisabled.value = false;
    }

    function onSelectMethod(method: IPaymentMethod) {
        if (isDisabled.value) return;

        props.onSelect(method);
    }

    function setPaymentPrompt(prompt: IMultiLanguageText | undefined) {
        paymentPrompt.value = prompt;
    }

    defineExpose({
        setPaymentPrompt,
        setPaymentMethods,
        setSelectedMethod,
        setDisabled,
        setEnabled,
    });
</script>

<style scoped lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    // out of flow, hanging above the selector's top edge: the selector is positioned at a
    // fixed height of the view, so a prompt in normal flow would push the method grid down
    // by its own height and move the buttons whenever a location configures a prompt
    .payment-prompt {
        position: absolute;
        bottom: 100%;
        left: 0;
        right: 0;

        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        font-weight: 600;
        color: #484848;
        text-align: center;
        font-size: 36px * variables.$kioskScale;
        padding: 0 20px 2.5em 20px;
    }

    .kiosk-payment-method-selector {
        display: grid;
        // columns are capped so the buttons don't grow with the view width
        grid-template-columns: repeat(2, minmax(0, 240px * variables.$kioskScale));
        justify-content: center;
        gap: 16px * variables.$kioskScale;
        padding: 12px * variables.$kioskScale 0;

        // a lone method would sit in the first of two tracks, so justify-content would
        // centre the pair and leave the button left of centre. one track centres it
        &.single {
            grid-template-columns: minmax(0, 240px * variables.$kioskScale);
        }

        &.disabled {
            opacity: 0.5;
        }

        > .method {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 10px;
            aspect-ratio: 1 / 1;
            padding: 10px;
            background: #fff;
            border: 1px solid colors.$greyBorder;
            border-radius: 8px;
            cursor: pointer;
            transition: border-color 0.15s linear;

            > .icon {
                width: 70%;
                height: 55%;
                background-position: center;
                background-repeat: no-repeat;
                background-size: contain;

                display: flex;
                align-items: center;
                justify-content: center;

                > i {
                    font-size: 44px * variables.$kioskScale;
                    color: colors.$fontColor;
                }
            }

            > .name {
                font-size: 20px * variables.$kioskScale;
                font-weight: 600;
                color: colors.$fontColor;
                text-align: center;
                word-break: break-word;
                margin-top: 0.5em;
            }

            &:not(:disabled):active {
                border-color: var(--colorPrimary);
            }

            // inset ring instead of a wider border, so marking does not shift the grid
            &.selected {
                border-color: var(--colorPrimary);
                box-shadow: inset 0 0 0 2px var(--colorPrimary);
            }

            &:disabled {
                cursor: default;
            }
        }
    }
</style>
